import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Menu as MenuIcon, X as CloseIcon } from "lucide-react";
import { path } from "../../../utils/common/constant";
import LanguageSelector from "../Select/LanguageSelector";
import { useTranslation } from "react-i18next";

interface MenuItem {
  key: string;
  label: string;
  path?: string;
  children?: Array<{
    key: string;
    label: string;
    path: string;
  }>;
}

const Header: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuItems: MenuItem[] = [
    {
      key: "home",
      label: t("header.navbar.home"),
      path: path.HOME,
    },
    {
      key: "apply-visa",
      label: t("header.navbar.apply_visa"),
      path: path.CHOOSE_COUNTRY,
      // children: [
      //   {
      //     key: "choose-country",
      //     label: "E-Visa For Individuals",
      //     path: path.CHOOSE_COUNTRY,
      //   },
      // ],
    },
    // {
    //   key: "visa-requirement",
    //   label: t("header.navbar.visa_requirements"),
    //   path: path.REQUIREMENTS,
    // children: [
    //   { key: "requirements", label: "Requirements", path: path.REQUIREMENTS },
    // ],
    // },
    // {
    //   key: "visa-status",
    //   label: t("header.navbar.visa_status"),
    //   path: path.PROFILE_SEARCH,
    // children: [
    //   {
    //     key: "profile-search",
    //     label: "Profile search",
    //   },
    //   {
    //     key: "instruction",
    //     label: "Instruction",
    //     path: path.SEARCH_INSTRUCTION,
    //   },
    // ],
    // },
    // {
    //   key: "news",
    //   label: t("header.navbar.news"),
    //   path: path.NEWS,
    // },
    {
      key: "about-us",
      label: t("about_us.title"),
      path: path.ABOUT_US,
    },
    {
      key: "instruction",
      label: t("header.navbar.instruction"),
      path: path.INSTRUCTION,
    },
    {
      key: "emergency-inquiry",
      label: t("emergency_visa.title"),
      path: path.EMERGENCY_INQUIRY,
    },
    {
      key: "faq",
      label: t("header.navbar.faq"),
      path: path.FAQ,
    },
    {
      key: "contact",
      label: t("header.navbar.contact"),
      path: path.CONTACT,
    },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false); // Close menu on navigation
  };

  return (
    <>
      <div className="w-full border-b border-gray-200 bg-white px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div
            className="cursor-pointer text-2xl font-bold text-primary-blue"
            onClick={() => handleNavigate(path.HOME)}
          >
            E-Visa To Vietnam
          </div>

          {/* Navigation */}
          <div className="hidden xl:flex items-center space-x-4">
            {menuItems.map((item) => (
              <div
                key={item.key}
                className="relative group font-bold text-[15px]"
              >
                <div
                  className="flex cursor-pointer items-center space-x-1 text-primary-blue hover:text-primary-yellow"
                  onClick={() => item.path && handleNavigate(item.path)}
                >
                  <span className="uppercase">{item.label}</span>
                  {item.children && <ChevronDown className="h-4 w-4" />}
                </div>

                {item.children && (
                  <div className="absolute hidden group-hover:block w-48 rounded-md border border-gray-200 bg-white py-2 shadow-lg top-full">
                    {item.children.map((child) => (
                      <div
                        key={child.key}
                        className="cursor-pointer px-4 py-2 text-sm text-primary-blue hover:text-primary-yellow"
                        onClick={() => handleNavigate(child.path)}
                      >
                        {child.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center space-x-4">
            {/* <img
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Flag_of_the_United_States.svg/800px-Flag_of_the_United_States.svg.png"
            alt="US Flag"
            className="hidden sm:block h-6 object-cover"
          /> */}
            <LanguageSelector />
            <button
              type="button"
              className="hidden sm:flex items-center bg-blue-100 hover:bg-blue-200 text-primary-blue border-none px-4 py-3 rounded-full font-semibold transition"
              onClick={() => handleNavigate(path.TRANSPORTATION)}
            >
              <img
                src="https://play-lh.googleusercontent.com/JG6IP-glhBT0E1EVRD2y6YvGNA4kr1ZW2U1HyWxRPhdlRY54Lu1RJeGRWdKWJuFn2lo"
                alt="Transportation"
                className="h-6 w-6 mr-2 object-contain rounded-full"
              />
              {t("header.navbar.transportation")}
            </button>
            <button
              type="button"
              className="hidden sm:flex bg-primary-lightyellow hover:bg-primary-yellow text-primary-blue border-none px-4 py-3 rounded-full font-semibold"
              onClick={() => handleNavigate(path.CHOOSE_COUNTRY)}
            >
              {t("cta.apply_visa")}
            </button>

            <MenuIcon
              className="h-6 w-6 xl:hidden text-primary-blue cursor-pointer"
              onClick={() => setIsMobileMenuOpen(true)}
            />
          </div>
        </div>
      </div>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
          <div className="w-2/3 max-w-xs bg-white h-full shadow-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-xl font-bold">Menu</span>
              <CloseIcon
                className="h-6 w-6 cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              />
            </div>
            <div className="flex flex-col space-y-4 mx-4">
              {menuItems.map((item) => (
                <div
                  key={item.key}
                  className="text-lg font-medium text-primary-blue cursor-pointer uppercase"
                  onClick={() => handleNavigate(item.path!)}
                >
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Bottom Line with Visa Process Message */}
      {/* <div className="text-center text-primary-blue font-regular text-lg bg-primary-lightyellow p-2">
        5 Minutes Visa Application:{" "}
        <span className="text-red-500 font-bold">SCAN - VERIFY - PAY</span>
      </div> */}
    </>
  );
};

export default Header;
